import classNames from 'classnames';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { FieldPath, FieldValues, useController, UseControllerProps, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DropdownPanelPortal, Icon, IconIdentifier, Tooltip } from '@steelbuy/ui-primitive';
import { InputType } from '../../Form.enums';

import './Select.scss';

type Option = { value: string | number; label: string };
type Options = Array<Option>;

type OptionsOrOptGroups = { options: Options; optGroups?: never } | { optGroups: Array<Options>; options?: never };

type SelectProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
    placeholder?: string;
    disabled?: boolean;
    error?: string;
    type?: InputType;
    searchable?: boolean;
} & UseControllerProps<TFieldValues, TName> &
    OptionsOrOptGroups;

export const Select = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    control,
    placeholder,
    disabled,
    options,
    optGroups,
    error,
    type = InputType.INLINE,
    rules,
    searchable,
    ...rest
}: SelectProps<TFieldValues, TName>) => {
    const { t } = useTranslation(['uiPrimitive']);

    const [isOpen, setIsOpen] = useState(false);
    const isOpenRef = useRef(false);
    const [filterText, setFilterText] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    // Only show the highlighted style when the user is actively using keyboard navigation
    const keyboardNavigating = useRef(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const fieldRef = useRef<HTMLSpanElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const filterInputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const { field } = useController({ control, rules, ...rest });
    const value = useWatch({ control, name: rest.name });

    // Build a flat list for option lookup (for label display)
    const allOptions: Options = optGroups ? optGroups.flat() : options ?? [];

    const selectedOption = allOptions.find((o) => o.value === value);
    const selectedLabel = selectedOption?.label;

    // Filter logic — applies to all options (flat or grouped)
    const isFiltering = searchable && filterText.length > 0;
    const matchesFilter = (opt: Option) => !isFiltering || opt.label.toLowerCase().includes(filterText.toLowerCase());

    const filteredOptions = options ? options.filter(matchesFilter) : undefined;
    const filteredOptGroups = optGroups ? optGroups.map((group) => group.filter(matchesFilter)) : undefined;
    const hasNoResults = isFiltering && allOptions.filter(matchesFilter).length === 0;

    const isEmpty =
        (options !== undefined && options.length === 0) ||
        (optGroups !== undefined && (optGroups.length === 0 || optGroups[0].length === 0));

    const closeDropdown = (notifyBlur = true, returnFocus = true) => {
        isOpenRef.current = false;
        setIsOpen(false);
        if (notifyBlur) field.onBlur();
        if (returnFocus) triggerRef.current?.focus();
    };

    // Close on outside click — closes when clicking outside the panel, including
    // clicks inside containerRef that are not on the trigger itself (e.g. label area)
    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            const target = e.target as Node;
            const inPanel = panelRef.current?.contains(target);
            const onTrigger = triggerRef.current?.contains(target);
            // Stay open only if clicking the trigger (handled by onClick toggle) or inside panel
            if (!inPanel && !onTrigger) {
                closeDropdown(true);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
            return () => document.removeEventListener('mousedown', handleOutsideClick);
        }
        return undefined;
    }, [isOpen]);

    // Reset filter and highlight when closed
    useEffect(() => {
        if (!isOpen) {
            setFilterText('');
            setHighlightedIndex(-1);
            keyboardNavigating.current = false;
        }
    }, [isOpen]);

    // Flat list of currently visible options for keyboard navigation
    const navigableOptions: Options = (() => {
        if (filteredOptions !== undefined) return filteredOptions;
        if (filteredOptGroups !== undefined) return filteredOptGroups.flat();
        return [];
    })();

    // Reset highlight when filter changes
    useEffect(() => {
        setHighlightedIndex(navigableOptions.length > 0 ? 0 : -1);
    }, [filterText]);

    // Scroll the highlighted item into view within the dropdown <ul>.
    // When searchable, the filter input is position:sticky at the top, so we
    // subtract its height from the top boundary to avoid items scrolling under it.
    useEffect(() => {
        if (highlightedIndex < 0) return;
        const listId = `form-select-listbox-${rest.name}`;
        const itemId = `form-select-opt-${rest.name}-${highlightedIndex}`;
        const list = document.getElementById(listId);
        const item = document.getElementById(itemId);
        if (!item || !list) return;
        const filterEl = list.querySelector<HTMLElement>('.form-select__field__list__filter');
        const stickyHeight = filterEl ? filterEl.offsetHeight : 0;
        const itemTop = item.offsetTop - list.offsetTop - stickyHeight;
        const itemBottom = item.offsetTop - list.offsetTop + item.offsetHeight;
        if (list.scrollTop > itemTop) {
            list.scrollTop = itemTop;
        } else if (list.scrollTop + list.clientHeight < itemBottom) {
            list.scrollTop = itemBottom - list.clientHeight;
        }
    }, [highlightedIndex]);

    const openDropdown = () => {
        if (disabled) return;
        isOpenRef.current = true;
        setIsOpen(true);
        const currentIndex = navigableOptions.findIndex((o) => o.value === value);
        setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
    };

    const handleToggle = () => {
        if (isOpen) {
            closeDropdown();
        } else {
            openDropdown();
        }
    };

    const handleSelect = (optValue: string | number) => {
        field.onChange(optValue);
        closeDropdown(true);
    };

    const handleClearPlaceholder = () => {
        field.onChange('');
        closeDropdown(true);
    };

    const navigateOptions = (direction: 1 | -1) => {
        keyboardNavigating.current = true;
        if (!isOpen) {
            openDropdown();
            return;
        }
        setHighlightedIndex((prev) => {
            const max = navigableOptions.length - 1;
            if (max < 0) return -1;
            if (prev < 0) return direction === 1 ? 0 : max;
            return Math.max(0, Math.min(max, prev + direction));
        });
    };

    const confirmHighlighted = () => {
        if (!isOpen || highlightedIndex < 0) return;
        const opt = navigableOptions[highlightedIndex];
        if (opt) handleSelect(opt.value);
    };

    const handleTriggerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                navigateOptions(1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                navigateOptions(-1);
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (isOpen) {
                    confirmHighlighted();
                } else {
                    openDropdown();
                }
                break;
            case 'Tab':
                if (isOpen) closeDropdown(true, false);
                break;
            case 'Escape':
                closeDropdown(true);
                break;
            default:
                break;
        }
    };

    const focusNextFromTrigger = (reverse = false) => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        // Walk up to find the container element in the actual document, then
        // find all focusable elements and move to the one after/before the trigger
        const focusable = Array.from(
            document.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
        ).filter((el) => !el.closest('[aria-hidden="true"]'));
        const triggerIndex = focusable.indexOf(trigger);
        if (triggerIndex < 0) return;
        const nextIndex = reverse ? triggerIndex - 1 : triggerIndex + 1;
        if (nextIndex >= 0 && nextIndex < focusable.length) {
            focusable[nextIndex].focus();
        }
    };

    const handleFilterKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                navigateOptions(1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                navigateOptions(-1);
                break;
            case 'Enter':
                e.preventDefault();
                confirmHighlighted();
                break;
            case 'Tab':
                // Prevent default — the filter input is in document.body (portal) so natural
                // Tab would go to the wrong element. Instead close, focus trigger, then move
                // to the next focusable element from the trigger's position in the real DOM.
                e.preventDefault();
                closeDropdown(true, false);
                triggerRef.current?.focus();
                focusNextFromTrigger(e.shiftKey);
                break;
            case 'Escape':
                closeDropdown(true);
                break;
            default:
                break;
        }
    };

    const rootClassMap = {
        'form-select': true,
        'form-select--disabled': !!disabled,
    };

    const fieldClassMap = {
        'form-select__field': true,
        'form-select__field--block': type === InputType.BLOCK,
        'form-select__field--error': !!error,
        'form-select__field--disabled': !!disabled,
        'form-select__field--open': isOpen,
    };

    const triggerClassMap = {
        'form-select__field__trigger': true,
        'form-select__field__trigger--block': type === InputType.BLOCK,
        'form-select__field__trigger--disabled': !!disabled,
        'form-select__field__trigger--placeholder': !selectedLabel && !isEmpty,
    };

    const renderDivider = () => (
        // eslint-disable-next-line jsx-a11y/role-supports-aria-props
        <li className="form-select__field__list__divider" aria-disabled="true">
            {'—'.repeat(8)}
        </li>
    );

    const renderOption = (opt: Option, navIndex: number) => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
        <li
            key={opt.value}
            id={`form-select-opt-${rest.name}-${navIndex}`}
            role="option"
            aria-selected={opt.value === value}
            className={classNames('form-select__field__list__option', {
                'form-select__field__list__option--selected': opt.value === value,
                'form-select__field__list__option--highlighted':
                    navIndex === highlightedIndex && keyboardNavigating.current,
            })}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleSelect(opt.value)}
            onMouseEnter={() => {
                keyboardNavigating.current = false;
                setHighlightedIndex(navIndex);
            }}
        >
            <span>{opt.label}</span>
            {opt.value === value && <Icon name={IconIdentifier.OK} />}
        </li>
    );

    const renderListContent = () => {
        if (hasNoResults) {
            return <li className="form-select__field__list__no-options">{t('uiPrimitive:dropdown.noOptions')}</li>;
        }

        if (filteredOptions !== undefined) {
            return filteredOptions.map((opt, i) => renderOption(opt, i));
        }

        if (filteredOptGroups !== undefined) {
            // navIndex must be global across all groups so keyboard navigation aligns
            let navIndex = 0;
            return filteredOptGroups.map((group, groupIdx) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={groupIdx} className="form-select__field__list__group">
                    <ul className="form-select__field__list__group__options">
                        {groupIdx > 0 && renderDivider()}
                        {group.map((opt) => {
                            const idx = navIndex;
                            navIndex += 1;
                            return renderOption(opt, idx);
                        })}
                    </ul>
                </li>
            ));
        }

        return null;
    };

    const displayLabel = isEmpty ? placeholder : selectedLabel ?? placeholder ?? '';

    const isPlaceholder = !selectedLabel || isEmpty;

    return (
        <div className={classNames(rootClassMap)} ref={containerRef}>
            <span className={classNames(fieldClassMap)} ref={fieldRef}>
                {/* Trigger button */}
                <div
                    ref={triggerRef}
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    aria-controls={`form-select-listbox-${rest.name}`}
                    aria-activedescendant={
                        isOpen && highlightedIndex >= 0 ? `form-select-opt-${rest.name}-${highlightedIndex}` : undefined
                    }
                    tabIndex={disabled ? -1 : 0}
                    className={classNames(triggerClassMap, {
                        'form-select__field__trigger--placeholder': isPlaceholder,
                    })}
                    onClick={handleToggle}
                    onKeyDown={handleTriggerKeyDown}
                    onBlur={(e) => {
                        // Don't notify RHF of blur while the dropdown is open — focus moving
                        // to the portal panel would trigger 'mode: all' validation prematurely.
                        // isOpenRef is updated synchronously before React re-renders, so it's
                        // always current here even on the first open.
                        if (isOpenRef.current) return;
                        const focusingIntoPanel = panelRef.current?.contains(e.relatedTarget as Node);
                        if (!focusingIntoPanel) {
                            field.onBlur();
                        }
                    }}
                >
                    {/*
                     * Sizer spans — invisible, zero-height, full-width contribution.
                     * Forces the trigger to be at least as wide as the longest option
                     * label (including placeholder), matching native <select> behaviour.
                     */}
                    <span className="form-select__field__trigger__sizer" aria-hidden="true">
                        {placeholder && <span className="form-select__field__trigger__sizer__item">{placeholder}</span>}
                        {allOptions.map((o) => (
                            <span key={o.value} className="form-select__field__trigger__sizer__item">
                                {o.label}
                            </span>
                        ))}
                    </span>
                    <span className="form-select__field__trigger__label">{displayLabel}</span>
                </div>

                <Icon name={isOpen ? IconIdentifier.CHEVRON_UP : IconIdentifier.CHEVRON_DOWN} />

                {!!error && (
                    <span className="icon__position">
                        {type !== InputType.BLOCK ? (
                            <Tooltip content={error}>
                                <div className="icon__position">
                                    <Icon name={IconIdentifier.ERROR} />
                                </div>
                            </Tooltip>
                        ) : (
                            <div className="icon__position">
                                <Icon name={IconIdentifier.ERROR} />
                            </div>
                        )}
                    </span>
                )}

                {/* Dropdown panel — rendered into document.body via portal so it never
                    affects table row height or any other parent layout */}
            </span>

            <DropdownPanelPortal
                triggerRef={fieldRef}
                isOpen={isOpen}
                panelRef={panelRef}
                onMount={searchable ? () => filterInputRef.current?.focus() : undefined}
                className="form-select__panel"
                onScrollClose={() => closeDropdown(false)}
            >
                <ul
                    ref={listRef}
                    role="listbox"
                    id={`form-select-listbox-${rest.name}`}
                    className={classNames('form-select__field__list', {
                        'form-select__field__list--block': type === InputType.BLOCK,
                    })}
                    onMouseLeave={() => setHighlightedIndex(-1)}
                >
                    {/* Filter input */}
                    {searchable && (
                        <li className="form-select__field__list__filter">
                            <input
                                ref={filterInputRef}
                                type="text"
                                className="form-select__field__list__filter__input"
                                placeholder={t('uiPrimitive:dropdown.filterPlaceholder')}
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={handleFilterKeyDown}
                            />
                        </li>
                    )}

                    {/* Placeholder option */}
                    {placeholder && !filterText && (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                        <li
                            role="option"
                            aria-selected={isPlaceholder}
                            className={classNames('form-select__field__list__option', {
                                'form-select__field__list__option--placeholder': true,
                            })}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={!rules?.required ? handleClearPlaceholder : undefined}
                        >
                            {placeholder}
                        </li>
                    )}

                    {renderListContent()}
                </ul>
            </DropdownPanelPortal>

            {!!error && type === InputType.BLOCK && <span className="form-select__field__error-msg">{error}</span>}
        </div>
    );
};
